import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import MultiStepForm from './MultiStepForm';
import { ApplicationFormData } from '../types/application';
import { firebaseAPI } from '../services/firebase';
import { getBrand } from '../config/brands';

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${props => props.theme.secondaryColor} 0%, #c3cfe2 100%);
  padding: 10px;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const Header = styled.header`
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  padding: 1rem 0;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  flex-direction: column;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 0 2rem;
    flex-direction: row;
    text-align: left;
  }
  
  .logo {
    font-size: 1.2rem;
    font-weight: bold;
    color: ${props => props.theme.primaryColor};
    cursor: pointer;
    text-decoration: none;
    transition: opacity 0.2s;
    margin-bottom: 0.5rem;
    
    @media (min-width: 768px) {
      font-size: 1.5rem;
      margin-bottom: 0;
    }
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  .subtitle {
    margin-left: 0;
    color: #6b7280;
    font-size: 0.9rem;
    
    @media (min-width: 768px) {
      margin-left: 2rem;
      font-size: 1rem;
    }
  }
`;

const SuccessContainer = styled.div`
  max-width: 600px;
  margin: 20px auto;
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  @media (min-width: 768px) {
    margin: 50px auto;
    padding: 40px;
  }
  
  .success-icon {
    font-size: 4rem;
    color: #4caf50;
    margin-bottom: 20px;
  }
  
  h2 {
    color: ${props => props.theme.primaryColor};
    margin-bottom: 15px;
  }
  
  .application-id {
    font-family: monospace;
    background: #f0f0f0;
    padding: 10px;
    border-radius: 4px;
    margin: 20px 0;
    font-weight: bold;
  }
  
  .next-steps {
    text-align: left;
    margin-top: 30px;
    
    h3 {
      color: ${props => props.theme.primaryColor};
      margin-bottom: 15px;
    }
    
    ul {
      color: #666;
      line-height: 1.8;
    }
  }
  
  .contact-info {
    background: #f9fafb;
    padding: 20px;
    border-radius: 8px;
    margin-top: 30px;
    border-left: 4px solid ${props => props.theme.primaryColor};
    
    h4 {
      color: ${props => props.theme.primaryColor};
      margin-bottom: 10px;
    }
    
    p {
      color: #666;
      margin: 5px 0;
    }
    
    a {
      color: ${props => props.theme.primaryColor};
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const ErrorContainer = styled.div`
  max-width: 600px;
  margin: 20px auto;
  background: white;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #ef4444;
  
  @media (min-width: 768px) {
    margin: 50px auto;
    padding: 40px;
  }
  
  .error-icon {
    font-size: 4rem;
    color: #ef4444;
    margin-bottom: 20px;
  }
  
  h2 {
    color: #ef4444;
    margin-bottom: 15px;
  }
  
  .retry-button {
    background: ${props => props.theme.primaryColor};
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 1rem;
    margin-top: 20px;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

interface RegistrationPageProps {}

const RegistrationPage: React.FC<RegistrationPageProps> = () => {
  const [searchParams] = useSearchParams();
  const [submissionState, setSubmissionState] = useState<{
    status: 'form' | 'loading' | 'success' | 'error';
    data?: any;
    error?: string;
  }>({ status: 'form' });

  const brandRef = searchParams.get('ref');
  const brand = getBrand(brandRef || undefined);

  const theme = {
    primaryColor: brand.primaryColor,
    secondaryColor: brand.secondaryColor
  };

  const handleSubmit = async (formData: ApplicationFormData) => {
    console.log('📝 Form submission started...');
    setSubmissionState({ status: 'loading' });
    
    try {
      // Add brand information to form data
      const dataWithBrand = {
        ...formData,
        brandRef: brandRef || 'finaconnect',
        brandName: brand.name
      };

      console.log('📝 Calling Firebase API with data:', { 
        businessEmail: dataWithBrand.businessEmail,
        brandRef: dataWithBrand.brandRef 
      });
      const response = await firebaseAPI.submitApplication(dataWithBrand);
      console.log('📝 Firebase API response:', response);
      
      if (response.success) {
        setSubmissionState({
          status: 'success',
          data: response.data
        });
      } else {
        setSubmissionState({
          status: 'error',
          error: response.error || 'Submission failed'
        });
      }
    } catch (error: any) {
      console.error('📝 Form submission error:', error);
      setSubmissionState({
        status: 'error',
        error: error.message || 'Network error occurred'
      });
    }
  };

  const resetForm = () => {
    setSubmissionState({ status: 'form' });
  };

  const handleLogoClick = () => {
    const landingPath = brandRef 
      ? `/${brandRef}` 
      : '/';
    window.location.href = `https://finaconnect.com${landingPath}`;
  };

  return (
    <ThemeProvider theme={theme}>
      <AppContainer>
        {brandRef !== 'tgpay' && (
          <Header>
            <HeaderContent>
              <div className="logo" onClick={handleLogoClick}>
                {brand.logo ? (
                  <img src={brand.logo} alt={brand.name} style={{ height: '40px', maxWidth: '200px', objectFit: 'contain' }} />
                ) : (
                  brand.name
                )}
              </div>
              <div className="subtitle">Merchant Onboarding Application</div>
            </HeaderContent>
          </Header>
        )}

        {submissionState.status === 'form' && (
          <MultiStepForm onSubmit={handleSubmit} brand={brand} />
        )}

        {submissionState.status === 'loading' && (
          <div style={{ textAlign: 'center', padding: '100px' }}>
            <div style={{ fontSize: '2rem', color: theme.primaryColor }}>
              ⏳ Submitting your application...
            </div>
          </div>
        )}

        {submissionState.status === 'success' && submissionState.data && (
          <SuccessContainer>
            <div className="success-icon">✅</div>
            <h2>Application Submitted Successfully!</h2>
            <p>Thank you for applying with {brand.name}. Your application has been received and is being processed.</p>
            
            <div className="application-id">
              Application ID: APP{submissionState.data.applicationId}
            </div>
            
            <div className="next-steps">
              <h3>What happens next?</h3>
              <ul>
                <li>Our team will review your application within 1-2 business days</li>
                <li>You'll receive an email confirmation shortly</li>
                <li>We may contact you if additional documentation is needed</li>
                <li>Once approved, you'll receive your merchant account details</li>
              </ul>
            </div>

            <div className="contact-info">
              <h4>Need assistance?</h4>
              <p>Email: {brand.contactEmail}</p>
              <p>Website: <a href={brand.website} target="_blank" rel="noopener noreferrer">{brand.website}</a></p>
              <p>Reference your Application ID for faster support</p>
            </div>
          </SuccessContainer>
        )}

        {submissionState.status === 'error' && (
          <ErrorContainer>
            <div className="error-icon">❌</div>
            <h2>Submission Failed</h2>
            <p>We encountered an error while processing your application:</p>
            <p style={{ color: '#ef4444', fontWeight: 'bold', margin: '20px 0' }}>
              {submissionState.error}
            </p>
            <p>Please try again or contact our support team if the problem persists.</p>
            <button className="retry-button" onClick={resetForm}>
              Try Again
            </button>
          </ErrorContainer>
        )}
      </AppContainer>
    </ThemeProvider>
  );
};

export default RegistrationPage;